/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-04 16:37:47
 **/

import { useMemo } from "react";
import { useTheme } from "@emotion/react";

import { _T } from "@rfice/language";
import { Maybe, SwitchCase } from "@/frameworks/libraries/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { Button, Column, Icon, Row, Typography } from "@/frameworks/designStuff/components";
import { usePhoneCall, useSidebarActions } from "@/frameworks/core/hooks";
import { Avatar, ScreenShareVideo, UserVideoWithAvatar } from "@/frameworks/core/components";

type MediaLayoutProps = {
	layout: PushServices.Call.UITypes;
	callees: string[];
} & Partial<{
	callID: string;
}>;

// const LOGTAG = "#SingleCallMediaLayout#";

export default function SingleCallMediaLayout({ layout, callees, ...props }: MediaLayoutProps) {
	const { colors } = useTheme();
	const sidebarActions = useSidebarActions();
	const phoneCall = usePhoneCall();
	const callID = props.callID ?? "";
	const call = phoneCall.state.call(callID)[0];
	const callUsers = phoneCall.state.users(callID)[0];

	const myInfo = useMemo(() => callUsers?.[Configure.userID], [callUsers]);
	const peerInfo = useMemo(() => {
		if (callUsers) {
			if (callees[0]) {
				return callUsers[callees[0]];
			}
		}
		return null;
	}, [callees]);

	const endScreenShare = () => {
		phoneCall.screen_update(callID, Configure.userID, "stop");

		if (sidebarActions.isOpen("PhoneDetail")) {
			sidebarActions.hide("PhoneDetail");
		}

		phoneCall.popup.show({
			callID,
			callerID: call.callerId,
			uiType: "floating"
		});
	};

	const renderAvatar = (callID: string, userId: string) => {
		return (
			<Row style={{ width: "100%" }} horizonAlign="center" verticalAlign="center">
				<Avatar id={userId} kind="people_80" useStatus={false} showMiniProfile={false} />
				{callID && (
					<UserVideoWithAvatar
						callID={callID}
						style={{ display: "none" }}
						userID={userId}
						kind="people_80"
						isSpeaking={true}
						isExpandIcon={true}
					/>
				)}
			</Row>
		);
	};

	return (
		<SwitchCase
			condition={layout}
			cases={{
				AVATARONLY: renderAvatar(callID || peerInfo?.callID || "", callees[0] || ""),
				VIDEOCALL: (
					<Column id="parent" style={{ width: "100%", paddingTop: "16px" }} gap={8} horizonAlign="center">
						<UserVideoWithAvatar
							callID={callID || ""}
							style={{ width: "320px", height: "180px" }}
							userID={myInfo?.userId || ""}
							kind="people_80"
							isExpandIcon={true}
						/>
						<UserVideoWithAvatar
							callID={callID || ""}
							style={{ width: "320px", height: "180px" }}
							userID={peerInfo?.userId || ""}
							kind="people_80"
							isExpandIcon={true}
						/>
					</Column>
				),
				SCREENSHARE: (
					<Column id="parent" style={{ width: "100%" }} gap={8}>
						<Maybe
							test={peerInfo?.media?.video === "on" || myInfo?.media?.video === "on"}
							fallback={renderAvatar(callID || peerInfo?.callID || "", callees[0] || Configure.userID)}
						>
							<Row
								style={{ width: "100%", paddingTop: "16px" }}
								gap={8}
								verticalAlign="center"
								horizonAlign="center"
							>
								<Column style={{ width: "50%" }}>
									<UserVideoWithAvatar
										callID={callID || ""}
										style={{ width: "176px", height: "99px" }}
										userID={myInfo?.userId || ""}
										kind="people_80"
										isExpandIcon={true}
									/>
								</Column>
								<Column style={{ width: "50%" }}>
									<UserVideoWithAvatar
										callID={callID || ""}
										style={{ width: "176px", height: "99px" }}
										userID={peerInfo?.userId || ""}
										kind="people_80"
										isExpandIcon={true}
									/>
								</Column>
							</Row>
						</Maybe>
						<Maybe
							test={peerInfo?.media?.screenShare}
							fallback={
								<Maybe test={myInfo?.media?.screenShare}>
									<Column
										style={{
											width: "100%",
											height: "220px",
											backgroundColor: colors.neutral_60_100,
											borderRadius: "8px"
										}}
										gap={8}
										horizonAlign="center"
										verticalAlign="center"
									>
										<Row gap={6}>
											<Icon name="ScreenShare24" size={20} fill={colors.neutral_00_100} />
											<Typography typoColor="neutral_00_100" typoSize="Title3">
												{_T("내 화면 공유 중")}
											</Typography>
										</Row>
										<Button
											filled="neutral"
											style={{ borderRadius: "100px" }}
											onClick={endScreenShare}
										>
											{_T("공유종료")}
										</Button>
									</Column>
								</Maybe>
							}
						>
							<Row style={{ width: "100%" }} verticalAlign="center" horizonAlign="center">
								<ScreenShareVideo
									style={{ width: "346px", height: "204px" }}
									userID={
										myInfo?.media?.screenShare
											? myInfo?.userId ?? ""
											: peerInfo?.media?.screenShare
												? peerInfo?.userId ?? ""
												: ""
									}
									callID={callID ?? ""}
									isExpandIcon={true}
								/>
							</Row>
						</Maybe>
					</Column>
				)
			}}
		/>
	);
}
